import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';
import { nanoid } from 'nanoid';

export type ChatIntent = 'documentation' | 'feature-request' | 'bug-report' | 'mapping-assistant';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  isError?: boolean;
  /** A fixed opener, not generated — nothing to regenerate. */
  isCanned?: boolean;
  feedback?: 'up' | 'down';
};

export type SavedSession = {
  id: string;
  title: string;
  templateName?: string;
  intent: ChatIntent | null;
  messages: ChatMessage[];
  savedAt: number;
};

const HISTORY_KEY = 'mm_chat_history';
const MAX_SESSIONS = 20;

const loadHistory = (): SavedSession[] => {
  try {
    const saved = localStorage.getItem(HISTORY_KEY);
    return saved ? (JSON.parse(saved) as SavedSession[]) : [];
  } catch {
    return [];
  }
};

const persistHistory = (history: SavedSession[]) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch (e) { /* storage unavailable */ }
};

export type ChatSlice = {
  isOpen: boolean;
  isLoading: boolean;
  unreadCount: number;
  intent: ChatIntent | null;
  messages: ChatMessage[];
  history: SavedSession[];
  restoredFromId: string | null;
  setIsOpen: (open: boolean) => void;
  setIsLoading: (v: boolean) => void;
  setIntent: (intent: ChatIntent) => void;
  toggleChat: () => void;
  addMessage: (role: ChatMessage['role'], content: string, opts?: { isError?: boolean; isCanned?: boolean }) => void;
  removeMessage: (id: string) => void;
  setMessageFeedback: (id: string, feedback: 'up' | 'down') => void;
  saveSession: (templateName?: string) => void;
  loadSession: (id: string) => void;
  deleteSession: (id: string) => void;
  clearMessages: () => void;
};

export const createChatSlice: StateCreator<
  { chat: ChatSlice },
  [['zustand/immer', never]],
  [],
  ChatSlice
> = (_set, _get) => {
  const [set] = createLens(_set, _get, ['chat']);

  return {
    isOpen: false,
    isLoading: false,
    unreadCount: 0,
    intent: null,
    messages: [],
    history: loadHistory(),
    restoredFromId: null,
    setIsOpen: (open) =>
      set((s) => {
        s.isOpen = open;
        if (open) s.unreadCount = 0;
      }),
    setIsLoading: (v) =>
      set((s) => {
        s.isLoading = v;
      }),
    setIntent: (intent) =>
      set((s) => {
        s.intent = intent;
      }),
    toggleChat: () =>
      set((s) => {
        s.isOpen = !s.isOpen;
        if (s.isOpen) s.unreadCount = 0;
      }),
    addMessage: (role, content, opts) =>
      set((s) => {
        s.messages.push({ id: nanoid(), role, content, timestamp: Date.now(), isError: opts?.isError, isCanned: opts?.isCanned });
        s.restoredFromId = null;
        if (role === 'assistant' && !_get().chat.isOpen) {
          s.unreadCount += 1;
        }
      }),
    removeMessage: (id) =>
      set((s) => {
        s.messages = s.messages.filter((m) => m.id !== id);
      }),
    setMessageFeedback: (id, feedback) =>
      set((s) => {
        const msg = s.messages.find((m) => m.id === id);
        if (msg) msg.feedback = msg.feedback === feedback ? undefined : feedback;
      }),
    saveSession: (templateName) =>
      set((s) => {
        if (s.restoredFromId) return;
        const real = s.messages.filter((m) => !m.isError);
        if (real.length < 2) return;
        const firstUser = real.find((m) => m.role === 'user');
        if (!firstUser) return;
        const session: SavedSession = {
          id: nanoid(),
          title: firstUser.content.slice(0, 60),
          templateName,
          intent: s.intent,
          messages: [...s.messages],
          savedAt: Date.now(),
        };
        s.history = [session, ...s.history].slice(0, MAX_SESSIONS);
        persistHistory(s.history);
      }),
    loadSession: (id) =>
      set((s) => {
        const session = s.history.find((h) => h.id === id);
        if (!session) return;
        s.messages = [...session.messages];
        s.intent = session.intent;
        s.restoredFromId = id;
      }),
    deleteSession: (id) =>
      set((s) => {
        s.history = s.history.filter((h) => h.id !== id);
        persistHistory(s.history);
      }),
    clearMessages: () =>
      set((s) => {
        s.messages = [];
        s.intent = null;
        s.restoredFromId = null;
      }),
  };
};
